// @ts-nocheck
import { capitalize, clone, cloneDeep, compact, findLastIndex, flatten, isBoolean, isEmpty, uniq } from 'lodash';
import { GeoDictionary } from 'mercator-cartography-api-client/lib/interfaces/geoDictionary.types';
import moment, { Moment } from 'moment-timezone';
import { IntlShape } from 'react-intl';
import { getSonataSession } from 'sonata-static-storage/lib/helpers/session.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import {
  CampaignPropertyEntry,
  digitalConversionKpis,
  statsKeys,
  TableKpi,
  tableKpis
} from 'sonata-static-storage/lib/samples/statsPreferences.samples';

import { checkAdFormat } from '@/helpers/reporting.helpers';
import { StatsColumnsDictionary } from '@/ts-helpers/stats.helper';

import type { AdInterface } from 'sonata-static-storage/lib/schemas/ad/ad.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';
import type { CampaignItem } from '@/ts-modules/reporting/CampaignListSelector/CampaignListSelector';
import type { Columns } from '@/ts-modules/reporting/ReportDownload/types';
import type { CampaignsCustomEvents, StatsType } from '@/ts-types/reporting.types';

const retrieveVisibleHeadersFromSession = (statsType: StatsType): string[] => {
  try {
    const session = getSonataSession();
    const localStorageKey = `${statsType}VisibleHeaders`;
    const sessionData: Record<string, any>[] = JSON.parse(window.localStorage.campaignStats);
    const headersInSession = sessionData.find(sessionDataEntry => {
      return !isEmpty(sessionDataEntry[localStorageKey]);
    });
    if (!headersInSession) {
      return [];
    }
    return headersInSession[localStorageKey][session?.userFrom || session?.user || 'N/A'] as string[];
  } catch (_e: any) {
    return [];
  }
};

const obtainCampaignProperty = (o: CampaignItem, s: any): any => {
  s = s.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
  s = s.replace(/^\./, ''); // strip a leading dot
  const a = s.split('.');
  for (let i = 0, n = a.length; i < n; ++i) {
    const k = a[i];
    if (k in o) {
      o = o[k];
    } else {
      return;
    }
  }
  return o;
};

const checkCampaignProperty = (options: CampaignPropertyEntry[], campaigns: CampaignItem[]): boolean => {
  return campaigns.some(campaign => {
    return options.some(option => {
      if (
        option.notEmpty &&
        obtainCampaignProperty(campaign, option.key) &&
        !isEmpty(obtainCampaignProperty(campaign, option.key))
      ) {
        return true;
      }
      if (option.value !== undefined) {
        if (
          (isBoolean(option.value) && option.value === obtainCampaignProperty(campaign, option.key)) ||
          (!isBoolean(option.value) &&
            (option.value! as string[]).includes(obtainCampaignProperty(campaign, option.key)))
        ) {
          return true;
        }
      }
      return false;
    });
  });
};

const campaignHasDigitalConversionPixels = (campaign: CampaignItem): boolean => {
  return (
    campaign.userPixels &&
    campaign.userPixels.some((userPixel: Record<string, any>) => {
      return taptap.pixels.placementEnum.includes(userPixel.placement);
    })
  );
};

const campaignHasQRConfigured = (campaign: CampaignItem): boolean => {
  return campaign.ads.some(({ adOptions }: { adOptions: Record<string, any> }) => {
    return adOptions.qrConfig?.enabled;
  });
};

const campaignsHaveQRConfigured = (campaigns: CampaignItem[]): boolean => {
  return campaigns.some(campaign => campaignHasQRConfigured(campaign));
};

const campaignsHaveDigitalConversionPixelsOrAppInstallations = (campaigns: CampaignItem[]): boolean => {
  return campaigns.some(campaign => {
    const hasPixels = campaignHasDigitalConversionPixels(campaign);
    const { hasAppInstallsEnabled } = campaign;
    return hasPixels || hasAppInstallsEnabled;
  });
};

const getOldestCampaignBeginDate = (campaigns: CampaignItem[]): Moment => {
  let oldestBeginDate: Moment;
  campaigns.forEach(campaign => {
    const campaignBeginDate = moment.utc(campaign.beginDate);
    if (!oldestBeginDate || oldestBeginDate.isAfter(campaignBeginDate)) {
      oldestBeginDate = campaignBeginDate;
    }
  });
  return oldestBeginDate!;
};

const includedAdditionalFiltersHeaderKeys = (headerKeys: Record<string, any>[], currentFilters: string[]): void => {
  if (currentFilters.includes('creatId')) {
    headerKeys.splice(findLastIndex(headerKeys, hk => hk.filterType) + 1, 0, {
      enabled: true,
      filterAssociated: true,
      kpi: 'adCreatId',
      type: 'string',
      visible: true
    });
  }
  if (currentFilters.includes('cid')) {
    headerKeys.splice(findLastIndex(headerKeys, hk => hk.filterType) + 1, 0, {
      enabled: true,
      filterAssociated: true,
      kpi: 'campaignId',
      type: 'string',
      visible: true
    });
  }
  if (currentFilters.includes('screenId')) {
    headerKeys.splice(findLastIndex(headerKeys, hk => hk.filterType) + 1, 0, {
      enabled: true,
      filterAssociated: true,
      kpi: 'screenName',
      type: 'string',
      visible: true
    });
  }
};

const obtainTableHeaders = ({
  campaignEvents = [],
  campaigns,
  cartography,
  customEvents,
  stats,
  statsGroups,
  statsType,
  user,
  userRoles
}: {
  campaignEvents: string[];
  campaigns: CampaignItem[];
  cartography: Partial<GeoDictionary>[];
  customEvents: CampaignsCustomEvents;
  stats: Record<string, any>;
  statsGroups: string[];
  statsType: StatsType;
  user: Record<string, any>;
  userRoles: UserRoleInterface[];
}): (TableKpi & { customCopy: string; filterAssociated: boolean })[] => {
  const statsKpis: TableKpi[] = tableKpis[`${statsType}`];
  const headerKeys: Record<string, any>[] = [];
  const campaignsAds: AdInterface[] = campaigns.map(c => c.ads).flat();
  const filteredStatsKPis = statsKpis.filter(statsKpi => {
    if (statsKpi.enabled) {
      const hasValidCampaignType = Array.isArray(statsKpi.campaignType) && statsKpi.campaignType.length;
      if (
        hasValidCampaignType &&
        campaigns.filter(campaign => statsKpi.campaignType?.includes(campaign.type)).length === 0
      ) {
        return false;
      }
      if (statsKpi.permissions) {
        if (
          statsKpi.permissions.some(permission => {
            return !checkUserResourcePermissions(`reporting${capitalize(permission)}`, user, userRoles);
          })
        ) {
          return false;
        }
      }
      if (statsKpi.appInstallType) {
        return Object.keys(stats).some(statsKey => statsKeys.appInstallKeys.includes(statsKey));
      }
      if (statsKpi.onlyWithViewability) {
        if (!campaigns.some(campaign => campaign.hasViewability)) {
          return false;
        }
      }
      if (statsKpi.adFormat && !checkAdFormat(statsKpi.adFormat, campaignsAds, stats)) {
        return false;
      }
      if (statsKpi.customEvent && !campaigns.some(c => c.customEvents)) {
        return false;
      }
      if (statsKpi.pricingKpi) {
        const notKpiPermission = statsKpi.permissions?.some(permission => {
          return !checkUserResourcePermissions(`reporting${capitalize(permission)}`, user, userRoles);
        });
        const notDataFeesPermission = checkUserResourcePermissions('reportingreportingDataFees', user, userRoles);
        if (statsKpi.kpi === 'dataFee' && !user.canAvoidDataFees() && !notDataFeesPermission) {
          return false;
        }
        if (notKpiPermission) {
          return false;
        }
      }
      if (statsKpi.campaignProperty && !checkCampaignProperty(statsKpi.campaignProperty, campaigns)) {
        return false;
      }
      if (statsKpi.pixelType) {
        if (stats[statsKpi.kpi!]) {
          return !campaigns.some(campaign => campaignHasDigitalConversionPixels(campaign));
        }
        return false;
      }
      if (digitalConversionKpis.includes(statsKpi.kpi!) || statsKeys.appInstallKeys.includes(statsKpi.kpi!)) {
        if (stats[statsKpi.kpi!]) {
          return campaignsHaveDigitalConversionPixelsOrAppInstallations(campaigns);
        }
        return false;
      }
      if (statsKpi.limitByDate) {
        const parseLimitDate = moment.utc(statsKpi.limitDate);
        const oldestBeginDate = getOldestCampaignBeginDate(campaigns);
        if (parseLimitDate.isAfter(oldestBeginDate)) {
          return false;
        }
      }
      if ('aggregatedKpi' in statsKpi) {
        if (!checkUserResourcePermissions('reportingTotalFootfallAttribution', user, userRoles)) {
          return false;
        }
      }
      if (statsKeys.qr.includes(statsKpi.kpi!)) {
        return campaignsHaveQRConfigured(campaigns);
      }
      if (statsKpi.cartographicProperty) {
        return Object.entries(statsKpi.cartographicProperty).every(([property, expected]) => {
          const reportingProp = property as keyof GeoDictionary['reporting'];
          return cartography.every(countryConfig => {
            const reportingValue = countryConfig.reporting?.[reportingProp];
            return (expected === true && reportingValue === true) || (expected === false && reportingValue !== true);
          });
        });
      }
      return true;
    }
    return false;
  });
  filteredStatsKPis.forEach(statsKpi => {
    if (statsKpi.filterType) {
      statsGroups.forEach(filter => {
        headerKeys.push({
          activeMetricType: false,
          filterType: true,
          kpi: filter,
          visible: true
        });
      });
    } else if (statsKpi.activeMetricType) {
      headerKeys.push(
        ...campaignEvents
          .filter(e => e && !['impressions', 'footfall_attribution', 'qr', 'mediaFee'].includes(e))
          .map(e => {
            return {
              ...statsKpi,
              kpi: e
            };
          })
      );
    } else if (statsKpi.customEvent && statsType === 'stats') {
      const customEventsKpis = customEvents[statsKpi.kpi!.split('.')[1] as 'actions' | 'clicks' | 'impressions'];
      headerKeys.push(
        ...new Set([
          ...(customEventsKpis.map(customKpi => {
            return {
              ...clone(statsKpi),
              kpi: customKpi
            };
          }) || [])
        ])
      );
    } else if (statsKpi.pixelType) {
      headerKeys.push({
        cloneDeep: statsKpi,
        kpi: `${statsKpi.kpi}.${statsKpi.pixelProperty}`
      });
    } else {
      headerKeys.push(statsKpi);
    }
  });
  includedAdditionalFiltersHeaderKeys(headerKeys, statsGroups);
  return compact(flatten(headerKeys));
};

const tableHeaderIsVisible = ({
  header,
  headersFromSession,
  statsGroups,
  statsHeaders
}: {
  header: Record<string, any>;
  headersFromSession: string[];
  statsGroups: string[];
  statsHeaders: TableKpi[];
}): boolean => {
  let headerIsVisible = header.visible;
  const headerKey = header.kpi;
  const headerStorageKey = StatsColumnsDictionary[headerKey] || headerKey;
  const headerIsDateOrFilter = (header.filterType && statsGroups.includes(header.kpi)) || header.kpi === 'sdate';
  const headerIsActiveMetric = header.activeMetricType && header.kpi === 'impressions';
  const headerIsFilterAssociated = header.filterAssociated;
  if (headersFromSession?.length && !headerIsDateOrFilter && !headerIsActiveMetric && !headerIsFilterAssociated) {
    headerIsVisible = headersFromSession.includes(headerKey) || headersFromSession.includes(headerStorageKey);
  } else if (statsHeaders.length > 0 && !headerIsDateOrFilter && !headerIsActiveMetric && !headerIsFilterAssociated) {
    const foundHeader = statsHeaders.find(({ kpi }) => kpi === headerKey);
    headerIsVisible = foundHeader?.visible || false;
  }
  return headerIsVisible;
};

const getPixelHeaders = ({
  header,
  headersFromSession,
  intl,
  statsGroups,
  statsHeaders,
  userPixel
}: {
  header: Record<string, any>;
  headersFromSession: string[];
  intl: IntlShape;
  statsGroups: string[];
  statsHeaders: TableKpi[];
  userPixel: Record<string, any>;
}): TableKpi & { pixelData: Record<string, any> } => {
  return {
    activeMetricType: false,
    className: 'colPixel',
    cpy:
      header.pixelProperty === 'total'
        ? capitalize(userPixel.name)
        : intl.formatMessage({
            defaultMessage: 'CP',
            id: 'reporting.table.headers.cpyTableCp'
          }) + capitalize(userPixel.name),
    customEvent: false,
    filterType: false,
    pixelData: userPixel,
    pixelProperty: userPixel ? header.pixelProperty : 'none',
    prop: header.pixelProperty === 'total' ? userPixel.event : `cp${userPixel.event}`,
    type: header.pixelProperty === 'total' ? 'integer' : 'currency',
    visible: tableHeaderIsVisible({
      header: {
        ...cloneDeep(header),
        kpi: header.pixelProperty === 'total' ? userPixel.event : `cp${userPixel.event}`
      },
      headersFromSession,
      statsGroups,
      statsHeaders
    })
  };
};

const capitalizeHeaderString = (headerString: string): string => {
  let headerResult = capitalize(headerString);
  if (headerResult.indexOf('_') > -1) {
    let transformedString = '';
    headerResult.split('_').forEach(substring => {
      transformedString += capitalize(substring);
    });
    headerResult = transformedString;
  }
  return headerResult;
};

const getTableHeader = ({
  cartography,
  header,
  headersFromSession,
  intl,
  showGTotal,
  statsGroups,
  statsHeaders
}: {
  cartography: Record<string, any>;
  header: TableKpi & { customCopy: string; filterAssociated: boolean };
  headersFromSession: string[];
  intl: IntlShape;
  showGTotal: boolean;
  statsGroups: string[];
  statsHeaders: TableKpi[];
}): Record<string, any> => {
  const headerIsDateOrFilter = statsGroups.includes(header.kpi!) || header.kpi === 'sdate';
  const headerIsActiveMetric = header.kpi && header.kpi === 'impressions';
  let headerCopy =
    header.kpi &&
    intl.formatMessage({
      defaultMessage: header.kpi,
      id: `reporting.table.headers.cpyTable${capitalizeHeaderString(header.kpi)}`
    });
  if (header.kpi === 'cost' && showGTotal) {
    headerCopy = intl.formatMessage({
      defaultMessage: 'Media Fee',
      id: 'reporting.table.headers.cpyTableMediaFee'
    });
  }
  if (header.customCopy) {
    headerCopy = intl.formatMessage({
      defaultMessage: header.customCopy,
      id: `reporting.table.headers.${header.customCopy}`
    });
  }
  if (header.kpi?.includes('cartoPyramid')) {
    let tagId = header.kpi.split('cartoPyramid.')[1];
    tagId = tagId.charAt(0).toLowerCase() + tagId.slice(1);
    headerCopy = intl.formatMessage({
      defaultMessage: 'Cartography - ',
      id: 'reporting.table.headers.cpyFiltercartoPyramid'
    });
    headerCopy +=
      cartography.find((cartographyLevel: Record<string, any>) => cartographyLevel.id === tagId)?.name || 'N/A';
  }
  const headerInfo: Record<string, any> = {
    activeMetricType: headerIsActiveMetric,
    className: statsGroups.includes(header.kpi as string) ? 'filter' : `col${capitalize(header.kpi)}`,
    cpy:
      headerCopy ||
      header.kpi
        ?.split('_')
        .map(partialKey => capitalize(partialKey))
        .join(' '),
    customEvent: header.customEvent,
    filterAssociated: header.filterAssociated,
    filterType: headerIsDateOrFilter,
    prop: header.kpi,
    type: header.type,
    units: header.units,
    visible: tableHeaderIsVisible({
      header,
      headersFromSession,
      statsGroups,
      statsHeaders
    })
  };
  if (header.kpi === 'data_fee') {
    headerInfo.hasIcon = true;
    headerInfo.iconType = 'fa-question-circle';
  }
  if ('aggregatedKpi' in header) {
    headerInfo.aggregatedKpi = header.aggregatedKpi;
  }
  if ('avgKpi' in header) {
    headerInfo.avgKpi = header.avgKpi;
  }
  return headerInfo;
};

export const prepareTableHeaders = ({
  campaignEvents = [],
  campaigns,
  cartography,
  customEvents,
  intl,
  showGTotal = false,
  stats,
  statsGroups,
  statsType,
  user,
  userRoles
}: {
  activeMetric?: string;
  campaignEvents?: string[];
  campaigns: CampaignItem[];
  cartography: Partial<GeoDictionary>[];
  customEvents: CampaignsCustomEvents;
  intl: IntlShape;
  showGTotal?: boolean;
  stats: Record<string, any>;
  statsGroups: string[];
  statsType: StatsType;
  user: Record<string, any>;
  userRoles: UserRoleInterface[];
}): Columns[] => {
  let headers: Record<string, any>[] = [];
  const headersFromSession = retrieveVisibleHeadersFromSession(statsType);
  const statsHeaders = obtainTableHeaders({
    campaignEvents,
    campaigns,
    cartography,
    customEvents,
    stats,
    statsGroups,
    statsType,
    user,
    userRoles
  });
  statsHeaders.forEach(header => {
    if (header.pixelType) {
      flatten(
        uniq(
          campaigns.map(campaign => {
            return (campaign.userPixels as Record<string, any>[]).map(userPixel => {
              return userPixel._id;
            });
          })
        )
      ).forEach(userPixel => {
        headers.push(
          getPixelHeaders({
            header,
            headersFromSession,
            intl,
            statsGroups,
            statsHeaders,
            userPixel
          })
        );
        headers = flatten(headers);
      });
    } else {
      headers.push(
        getTableHeader({
          cartography,
          header,
          headersFromSession,
          intl,
          showGTotal,
          statsGroups,
          statsHeaders
        })
      );
    }
  });
  // @ts-ignore // todo lodash installed is v2 which is really behind latest versions
  return uniq(headers, 'prop');
};
